import { useEffect, useMemo, useState } from 'react';
import { GridInitialState } from '@mui/x-data-grid-pro';
import { CustomerMap, fetchCustomerMaps, getCustomerMapsSubject } from '../../../../../store/CustomerMap';
import { DefaultCustomerMapGridColumnDefs } from '../../ColumnDefs/CustomerMapGridColumnDefs';
import { UpstackDataGrid } from '../../../../Compounds/UpstackDataGrid/UpstackDataGrid';
import { getInitialState } from '../../../../Compounds/UpstackDataGrid/helpers';
import { PageCard } from '../../../../Compounds/CardWithTitle';
import {
  getCustomizationSubject,
  getDefaultPreferences,
  GridPreferences
} from '../../../../../store/UserCustomizations';
import { Icon } from '../../../../Atoms/Icon';
import { TextSpan } from '../../../../Atoms/Text';
import { ComponentSpinner } from '../../../../Compounds/Loading/ComponentSpinner';
import { ApiAdvancedFilter, PaginatedResponse } from '../../../../../store/GeneralStore';
import { NewMapDialog } from './NewMapDialog';

export const CustomerMapTab = () => {
  const [customerMapsLoading, setCustomerMapsLoading] = useState<boolean>(false);
  const [customerMaps, setCustomerMaps] = useState<PaginatedResponse<CustomerMap>>();
  const [isMapFormOpen, setIsMapFormOpen] = useState<boolean>(false);
  const [gridSettings, setGridSettings] = useState<GridPreferences>(
    getDefaultPreferences()?.content['customerMapGrid'] as GridPreferences
  );
  const [initialState, setInitialState] = useState<GridInitialState>();

  const filterKeys = useMemo(
    () => DefaultCustomerMapGridColumnDefs.filter((column) => column.filterable).map((column) => column.field),
    []
  );

  const handleFetchCustomerMaps = async (filters?: ApiAdvancedFilter, localLoading?: boolean, forceRefresh = false) => {
    if (localLoading) setCustomerMapsLoading(true);
    await fetchCustomerMaps(filters, forceRefresh);
    if (localLoading) setCustomerMapsLoading(false);
  };

  useEffect(() => {
    // Subscribe to customer maps
    const customerMapsSub = getCustomerMapsSubject().subscribe((customerMaps) => {
      setCustomerMaps(customerMaps);
    });
    const userCustSub = getCustomizationSubject().subscribe((userCust) => {
      if (userCust) {
        setGridSettings(userCust.content.customerMapGrid);

        const state = getInitialState(
          userCust.content.customerMapGrid,
          DefaultCustomerMapGridColumnDefs,
          'customerMapGrid'
        );
        setInitialState(state);

        const savedSort = state.sorting?.sortModel;
        const initialSortModel = savedSort?.length ? savedSort : undefined;
        handleFetchCustomerMaps(
          {
            orders: initialSortModel
              ? initialSortModel.map((sort) => ({ sort: sort.field, direction: sort.sort }))
              : undefined
          },
          true
        );
      }
    });

    return () => {
      if (customerMapsSub) customerMapsSub.unsubscribe();
      if (userCustSub) userCustSub.unsubscribe();
    };
  }, []);

  const handleNewMap = () => {
    setIsMapFormOpen(true);
  };

  const cardActions = () => (
    <div className="space-x-6 flex">
      <button
        className="flex items-center space-x-2"
        onClick={handleNewMap}
        data-cy="new-customer-map-button"
        data-testid="new-customer-map-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          New Customer Mapping
        </TextSpan>
      </button>
    </div>
  );

  return (
    <PageCard
      dataTestId="customer-map-management"
      dataCy="customer-map-management"
      childrenWrapperClass="min-h-80 max-w-[60vw] min-w-full"
      title="Customer Map"
      cardActions={cardActions()}>
      <>
        {!initialState ||
          (customerMapsLoading && (
            <div className="flex h-[45rem] m-auto">
              <ComponentSpinner label="Loading data..." />
            </div>
          ))}
        {initialState && !customerMapsLoading && (
          <UpstackDataGrid
            title="Customer Map"
            rows={customerMaps?.data || []}
            rowCount={customerMaps?.totalCount}
            columns={DefaultCustomerMapGridColumnDefs}
            advancedFiltering={true}
            apiFilter={true}
            fetchFunction={handleFetchCustomerMaps}
            filterKeys={filterKeys}
            page="customerMapGrid"
            gridSettings={gridSettings}
            showSearch={true}
            initialState={initialState}
            sxStyles={{ borderWidth: '0', borderBottomLeftRadius: '.75rem', borderBottomRightRadius: '.75rem' }}
            triggerSave={false}
            rowPrimaryKey={'customerId'}
          />
        )}
        <NewMapDialog
          open={isMapFormOpen}
          onClose={() => setIsMapFormOpen(false)}
        />
      </>
    </PageCard>
  );
};
